import type { TableCellProps } from '@mui/material';
import {
  Checkbox,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableHead,
  TableRow,
  Typography,
  Stack,
  TableSortLabel,
  styled,
  Skeleton,
  Box,
  Tooltip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import dayjs from 'dayjs';
import type { SortType, StorageUnit } from 'store/storageunits/types';
import {
  SortDirection,
  StorageUnitStatus,
  StorageUnitType,
} from 'store/storageunits/types';
import { useIsProject } from 'hooks/useIsProject';
import { splitName } from 'util/nameHelper';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useStorageUnits } from 'contexts/storageUnitContext';
import { useContextMenu } from '../contextMenu/context';
import { useDragAndDrop, useSelection } from '../context';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'store';
import {
  getLoading,
  getSortDirection,
  getSortType,
} from 'store/storageunits/selectors';
import { setSort } from 'store/storageunits/reducer';
import { useDebouncedLoading } from 'hooks/useDebouncedLoading';
import { isShared } from 'util/isSharedHelper';
import { useOnlineStatus } from 'hooks/useOnlineStatus';
import { storageUnitCache } from 'util/cache/storageUnitCacheIndexedDB';
import { isReadOnlyFolder } from 'util/access';
import { getStorageUnitIcon } from 'util/getStorageUnitIcon';
import { useIsExternal } from 'auth/useIsExternal';
import { hasOfflineFileData } from 'store/storageunits/utils';
import { useSelectableFileUnits } from '../hooks/useSelectableFileUnits';

import { ReactComponent as SharedIcon } from 'icons/shared.svg';
import OfflineStatusIndicator from './Cards/OfflineStatusIndicator';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const ReadOnlyStack = styled(Stack)(({ theme }) => ({
  color: theme.vars.palette.grey[600],
  '& svg': {
    color: theme.vars.palette.grey[600],
    fill: theme.vars.palette.grey[600],
  },
  '& svg path': {
    fill: theme.vars.palette.grey[600],
  },
}));

export type Column = {
  label: string;
  type?: SortType;
};

type CellProps = TableCellProps & { value: React.ReactNode };

const TableCell = ({ value, align }: CellProps) => (
  <MuiTableCell align={align}>
    {typeof value === 'string' ? <Typography>{value}</Typography> : value}
  </MuiTableCell>
);

const UnitTitle = ({ unit }: { unit: StorageUnit }) => {
  const isProject = useIsProject();
  const readOnly =
    unit.type === StorageUnitType.Folder && isReadOnlyFolder(unit);
  const TitleContainer = readOnly ? ReadOnlyStack : Stack;

  return (
    <TitleContainer direction='row' spacing={1}>
      {getStorageUnitIcon(unit, isProject)}
      <Typography>{splitName(unit.type, unit.name)}</Typography>
    </TitleContainer>
  );
};

const AccessCell = ({ unit }: { unit: StorageUnit }) => {
  if (!isShared(unit.access)) {
    return (
      <Box
        sx={{
          mr: 1.5,
        }}
      >
        -
      </Box>
    );
  }

  return (
    <Stack
      direction='row'
      spacing={1}
      sx={{
        alignItems: 'center',
      }}
    >
      <SharedIcon />
      <Typography>Shared</Typography>
    </Stack>
  );
};

const LockedStatus = ({ unit }: { unit: StorageUnit }) => {
  const locked = !!unit.exclusiveLease;

  if (!locked) {
    return <Typography>-</Typography>;
  }

  const lockedBy = unit.exclusiveLease?.userName;
  const tooltip = lockedBy ? `Locked by ${lockedBy}` : 'Locked';

  return (
    <Tooltip title={tooltip}>
      <LockOutlinedIcon fontSize='small' />
    </Tooltip>
  );
};

const FileStatus = ({ unit }: { unit: StorageUnit }) => {
  const { status, type } = unit;
  if (type === StorageUnitType.Folder) {
    return (
      <Box
        sx={{
          mr: 1.5,
        }}
      >
        -
      </Box>
    );
  }

  return (
    <>
      {status.includes(StorageUnitStatus.APPROVED) ? (
        <Typography>Approved</Typography>
      ) : (
        <Typography>Draft</Typography>
      )}
    </>
  );
};

const DownloadStatus = ({ unit }: { unit: StorageUnit }) => {
  const { type, id } = unit;
  if (type === StorageUnitType.Folder) {
    return <Typography>-</Typography>;
  }

  return (
    <OfflineStatusIndicator
      fileId={id}
      showPlaceholder
      hasLocalFileData={hasOfflineFileData(unit)}
    />
  );
};

const ValueRow = styled(TableRow)`
  cursor: pointer;
`;

const checkboxIconSx = {
  width: 18,
  height: 18,
  border: '2px solid',
  borderColor: 'grey.900',
  borderRadius: '3px',
  backgroundColor: 'background.paper',
  boxSizing: 'border-box' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 700,
  color: 'grey.900',
};

const ListRow = ({
  unit,
  showDownload,
  showLocked,
  showState,
}: {
  unit: StorageUnit;
  showDownload: boolean;
  showLocked: boolean;
  showState: boolean;
}) => {
  const { openContextMenu } = useContextMenu();
  const { openFile, openFolder } = useStorageUnits();
  const { dragAndDropProps, dropTarget } = useDragAndDrop(unit);
  const { selectedIds, isSelected, toggleSelected } = useSelection();
  const ref = useRef<HTMLTableRowElement | null>(null);
  const isExternal = useIsExternal();
  const checkIsProject = useIsProject();
  const { folderPath } = useSelector(
    (state: RootState) => state.storageUnitsState,
  );

  const readOnly =
    unit.type === StorageUnitType.Folder && isReadOnlyFolder(unit);

  const isOnline = useOnlineStatus();
  const [cacheChecked, setCacheChecked] = useState(false);
  const [isCached, setIsCached] = useState(false);
  const hasLocalFileData = hasOfflineFileData(unit);

  useEffect(() => {
    let mounted = true;

    if (unit.type !== StorageUnitType.File) {
      setIsCached(true);
      setCacheChecked(true);
      return () => {
        mounted = false;
      };
    }

    if (hasLocalFileData) {
      setIsCached(true);
      setCacheChecked(true);
      return () => {
        mounted = false;
      };
    }

    storageUnitCache
      .getOne(unit.id)
      .then((cached) => {
        if (!mounted) {
          return;
        }
        setIsCached(hasOfflineFileData(cached));
        setCacheChecked(true);
      })
      .catch(() => {
        if (!mounted) {
          return;
        }
        setIsCached(false);
        setCacheChecked(true);
      });

    return () => {
      mounted = false;
    };
  }, [unit.id, unit.type, hasLocalFileData, isOnline]);

  const isPendingOffline =
    unit.pendingAction === 'create' ||
    unit.pendingAction === 'update' ||
    unit.pendingAction === 'archive';
  const isAvailableOffline = hasLocalFileData || isCached;
  const disableInteractions =
    !isOnline &&
    unit.type === StorageUnitType.File &&
    (!cacheChecked || !isAvailableOffline) &&
    !isPendingOffline;

  const isProject = checkIsProject(unit);
  const isFileInProject =
    unit.type === StorageUnitType.File &&
    folderPath.some((ancestor) => checkIsProject(ancestor));
  const isDraggable =
    !unit.archived &&
    !readOnly &&
    !disableInteractions &&
    !(isExternal && (isProject || isFileInProject));

  const onContextMenu = (
    e: React.MouseEvent<HTMLElement>,
    unit: StorageUnit,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (readOnly || disableInteractions) {
      return;
    }

    if (!ref.current) {
      return;
    }

    openContextMenu(selectedIds.length > 0 ? null : unit, {
      x: e.pageX,
      y: e.pageY,
    });
  };

  const isFolder = unit.type === StorageUnitType.Folder;

  const onRowClicked = (id: string, type: StorageUnitType) => {
    if (disableInteractions || unit.archived) {
      return;
    }

    if (selectedIds.length > 0 && !isFolder) {
      toggleSelected(id);
      return;
    }

    if (type === StorageUnitType.File) {
      openFile(id);
    } else {
      openFolder(id);
    }
  };

  const checked = isSelected(unit.id);
  const dragProps = useMemo(() => {
    const props = { ...dragAndDropProps };
    if (disableInteractions) {
      (props as Record<string, unknown>).draggable = false;
    }
    return props;
  }, [dragAndDropProps, disableInteractions]);

  return (
    <ValueRow
      {...dragProps}
      ref={ref}
      hover
      role='checkbox'
      tabIndex={-1}
      key={unit.id}
      selected={dropTarget === unit.id || checked}
      onContextMenu={(e: React.MouseEvent<HTMLTableRowElement>) =>
        onContextMenu(e, unit)
      }
      onClick={() => onRowClicked(unit.id, unit.type)}
      draggable={isDraggable}
      sx={{
        opacity: disableInteractions ? 0.5 : 1,
        cursor: disableInteractions ? 'not-allowed' : 'pointer',
      }}
    >
      <MuiTableCell padding='checkbox'>
        {!isFolder && !disableInteractions && (
          <Checkbox
            checked={checked}
            onClick={(e) => {
              e.stopPropagation();
              toggleSelected(unit.id);
            }}
            icon={<Box sx={checkboxIconSx} />}
            checkedIcon={
              <Box sx={checkboxIconSx}>
                <CheckIcon sx={{ fontSize: 14 }} />
              </Box>
            }
            size='small'
            disableRipple
            sx={{ padding: 0 }}
          />
        )}
      </MuiTableCell>
      <TableCell value={<UnitTitle unit={unit} />} />
      <TableCell value={<AccessCell unit={unit} />} />
      <TableCell
        align='right'
        value={dayjs(unit.updatedAt ?? unit.createdAt).format('DD MMM YYYY')}
      />
      {showLocked && (
        <TableCell align='center' value={<LockedStatus unit={unit} />} />
      )}
      {showDownload && (
        <TableCell align='center' value={<DownloadStatus unit={unit} />} />
      )}
      {showState && (
        <TableCell align='right' value={<FileStatus unit={unit} />} />
      )}
    </ValueRow>
  );
};

type Props = {
  units: StorageUnit[];
  columns: Column[];
};

const StorageUnitList = ({ units, columns }: Props) => {
  const dispatch = useDispatch();
  const loading = useSelector(getLoading);
  const showLoading = useDebouncedLoading(loading);
  const direction = useSelector(getSortDirection);
  const type = useSelector(getSortType);
  const { selectedIds, isSelected, selectAll, clearSelection } = useSelection();
  const showLocked = columns.some((col) => col.label === 'Locked');
  const showDownload = columns.some((col) => col.label === 'Downloaded');
  const showState = columns.some((col) => col.label === 'State');
  const { isOnline, selectableFileIds, selectableFileUnits, selectionReady } =
    useSelectableFileUnits(units);

  useEffect(() => {
    if (isOnline || !selectionReady || selectedIds.length === 0) {
      return;
    }

    const nextSelectedIds = selectedIds.filter((id) =>
      selectableFileIds.has(id),
    );
    if (nextSelectedIds.length !== selectedIds.length) {
      if (nextSelectedIds.length) {
        selectAll(nextSelectedIds);
      } else {
        clearSelection();
      }
    }
  }, [
    clearSelection,
    isOnline,
    selectableFileIds,
    selectAll,
    selectedIds,
    selectionReady,
  ]);

  const allSelected =
    selectableFileUnits.length > 0 &&
    selectableFileUnits.every((u) => isSelected(u.id));
  const someSelected = selectableFileUnits.some((u) => isSelected(u.id));

  const handleSelectAll = () => {
    if (!selectableFileUnits.length) {
      return;
    }

    if (allSelected) {
      clearSelection();
    } else {
      selectAll(selectableFileUnits.map((u) => u.id));
    }
  };

  const onClick = (newType?: SortType) => {
    if (!newType) {
      return;
    }

    if (type !== newType) {
      dispatch(
        setSort({
          direction: SortDirection.ASC,
          type: newType,
        }),
      );
    } else {
      dispatch(
        setSort({
          direction:
            direction === SortDirection.ASC
              ? SortDirection.DESC
              : SortDirection.ASC,
          type,
        }),
      );
    }
  };

  const toMuiSortDirection = useMemo((): 'asc' | 'desc' => {
    return direction === SortDirection.ASC ? 'asc' : 'desc';
  }, [direction]);

  return (
    <Box
      sx={{
        pr: 6,
      }}
    >
      <Table stickyHeader aria-label='sticky table'>
        <TableHead>
          <TableRow>
            <MuiTableCell padding='checkbox'>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                disabled={!selectableFileUnits.length}
                onChange={handleSelectAll}
                icon={<Box sx={checkboxIconSx} />}
                checkedIcon={
                  <Box sx={checkboxIconSx}>
                    <CheckIcon sx={{ fontSize: 14 }} />
                  </Box>
                }
                indeterminateIcon={
                  <Box sx={checkboxIconSx}>
                    <RemoveIcon sx={{ fontSize: 14 }} />
                  </Box>
                }
                size='small'
                disableRipple
                sx={{ padding: 0 }}
              />
            </MuiTableCell>
            {columns.map((col: Column, i) => {
              const align =
                col.label === 'Locked' || col.label === 'Downloaded'
                  ? 'center'
                  : i > 1
                    ? 'right'
                    : 'left';

              return (
                <MuiTableCell
                  key={col.label}
                  sortDirection={toMuiSortDirection}
                  onClick={() => onClick(col.type)}
                  align={align}
                  sx={{ fontWeight: 'bold' }}
                >
                  {col.type ? (
                    <TableSortLabel
                      active={col.type === type}
                      direction={toMuiSortDirection}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </MuiTableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {showLoading
            ? Array.from(Array(12)).map((_, i) => (
                <ValueRow key={i}>
                  <MuiTableCell padding='checkbox' />
                  <TableCell value={<Skeleton variant='text' width={250} />} />
                  <TableCell value={<Skeleton variant='text' width={100} />} />
                  <TableCell
                    align='right'
                    value={
                      <Skeleton
                        variant='text'
                        width={100}
                        sx={{ marginLeft: 'auto' }}
                      />
                    }
                  />
                  {showLocked && (
                    <TableCell
                      align='center'
                      value={
                        <Skeleton
                          variant='text'
                          width={40}
                          sx={{ marginLeft: 'auto', marginRight: 'auto' }}
                        />
                      }
                    />
                  )}
                  {showDownload && (
                    <TableCell
                      align='center'
                      value={
                        <Skeleton
                          variant='text'
                          width={40}
                          sx={{ marginLeft: 'auto', marginRight: 'auto' }}
                        />
                      }
                    />
                  )}
                  {showState && (
                    <TableCell
                      align='right'
                      value={
                        <Skeleton
                          variant='text'
                          width={100}
                          sx={{ marginLeft: 'auto' }}
                        />
                      }
                    />
                  )}
                </ValueRow>
              ))
            : units.map((unit) => (
                <ListRow
                  key={unit.id}
                  unit={unit}
                  showLocked={showLocked}
                  showDownload={showDownload}
                  showState={showState}
                />
              ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default StorageUnitList;
